import Link from 'next/link'
import { useRouter } from 'next/router'
import { ChangeEvent, MouseEvent, useEffect, useState } from 'react'
import { ChevronLeft, Eye, EyeOff, Loader } from 'react-feather'
import { useAuth } from 'src/lib/contexts/AuthContext'
import { useFilter } from 'src/lib/contexts/FilterContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { validateEmail } from 'src/lib/utils/helpers'
import InputTextField from '../common/form-elements/InputTextField'
import { fetcher, getRequest, postRequest } from 'src/lib/core/api-client'
import { apiURI, constants } from 'src/lib/utils/constants'
import { getActiveStore } from 'src/lib/utils/onboarding'
import { useOnboarding } from 'src/lib/contexts/OnboardingContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import Cookies from 'js-cookie'
import { getXShopfloVersion, showMerchantInvoiceUnpaid } from 'src/lib/core/api-methods'
import { refresh, setVersionCookie, setInitialDateFilters } from '../../lib/utils/helpers'
import useReCaptcha from 'src/lib/hooks/useReCaptcha'
import OTPInput from '../common/OtpInput'

interface State {
  password: string
  showPassword: boolean
}

const Login = () => {
  const [values, setValues] = useState<State>({
    password: '',
    showPassword: false
  })
  const { login } = useAuth()
  const { setActiveShop, setOnboardingData, onboarding } = useOnboarding()
  const { setLoaderProperties, loaderConfig } = useLoader()
  const { t } = useLocale()
  const { setFiltersCtx } = useFilter()
  const { generateReCaptchaToken } = useReCaptcha()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')

  const [emailError, setEmailError] = useState<boolean>(false)
  const [passwordError, setPasswordError] = useState<boolean>(false)
  const [invoiceError, setInvoiceError] = useState<string>('')
  const [merchantId, setMerchantId] = useState<string>('')
  const [showOTPPage, setShowOTPPage] = useState<boolean>(false)
  const [otpValue, setOtpValue] = useState<string>('')
  const [otpError, setOtpError] = useState<boolean>(false)
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('')
  const [otpContext, setOtpContext] = useState<string>('')
  const [otpCounter, setOtpCounter] = useState<number>(0)

  // ** Hooks
  const router = useRouter()

  // useEffect(() => {
  //   if (hasAuthCookie()) router.replace('/')
  // }, [])

  useEffect(() => {
    setOtpError(false)
    if (otpValue.length === 6) {
      validateCredentials()
    }
  }, [otpValue])

  useEffect(() => {
    const timer: any = otpCounter > 0 && setInterval(() => setOtpCounter(otpCounter - 1), 1000)
    return () => clearInterval(timer)
  }, [otpCounter])

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword })
  }

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handlePasswordChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    if (passwordError) {
      setPasswordError(false)
    }
    setValues({ ...values, [prop]: event.target.value })
    setPassword(event.target.value)
  }

  // const handleCloseAlert = (_event: any, reason: string) => {
  //   if (reason === 'clickaway') {
  //     return
  //   }
  //   handleAlerts('')
  // }

  const checkForVersion = async () => {
    try {
      const versionResponse: any = await getXShopfloVersion()
      if (versionResponse?.data?.success) {
        if (Cookies.get(constants.FLO_SHOPFLO_VERSION) !== versionResponse?.data?.data?.version) {
          setVersionCookie(versionResponse?.data?.data?.version ?? 'stable')
          refresh()
        }
      } else {
        console.error(t('something_went_wrong'))
        setVersionCookie('stable')
      }
    } catch (err) {
      console.error(err)
    }
  }

  const callShowMerchantInvoiceUnpaid = async () => {
    try {
      await showMerchantInvoiceUnpaid(merchantId, {})
      errorToast(t('login.invoices_sent'))
    } catch (err) {
      console.error(t('something_went_wrong'))
    }
  }

  const validateCredentials = async () => {
    if (!validateEmail(email)) {
      setEmailError(true)
      return
    }
    if (password === null || password === '') {
      setPasswordError(true)
      return
    }
    const payload = {
      email: email.trim(),
      password: password,
      captcha_token: await generateReCaptchaToken('login')
    }

    try {
      setIsLoading(true)
      setLoaderProperties({ isLoaderVisible: true, message: ' ' })
      let response: any
      if (showOTPPage) {
        response = await postRequest('/merchant-user/verify', apiURI.AUTH_ENGINE, {
          otp: otpValue,
          context_id: otpContext
        })
      } else {
        response = await postRequest('/merchant-user/login', apiURI.AUTH_ENGINE, payload)
      }
      if (!response?.data?.success && showOTPPage) {
        if (response?.data?.error?.code === 'GE_001' || response?.data?.error?.code === 'TOO_MANY_REQUESTS') {
          setOtpErrorMessage(response?.data?.error?.message)
        } else {
          setOtpErrorMessage(t('something_went_wrong'))
        }
        setOtpError(true)
        setLoaderProperties({ isLoaderVisible: false })
        setIsLoading(false)
        return
      }
      if (!response?.data?.success && !showOTPPage) {
        if (response?.data?.error?.code === 'GE_001') {
          setInvoiceError(response?.data?.error?.message)
          setMerchantId(response?.data?.error?.data?.merchant_id)
        } else {
          errorToast(t('invalid_email_or_password'))
        }
        setLoaderProperties({ isLoaderVisible: false })
        setIsLoading(false)
        return
      }
      response = response?.data?.data
      if (response?.otp_required) {
        setOtpContext(response?.context_id)
        setShowOTPPage(true)
        setIsLoading(false)
        setOtpCounter(30)
        setLoaderProperties({ isLoaderVisible: false })
        return
      }
      const authToken = response?.access_token
      const authTokenExpiry = response?.access_token_expires_at
      const refreshToken = response?.refresh_token
      const refreshTokenExpiry = response?.refresh_token_expires_at
      login(authToken, email, '', refreshToken, authTokenExpiry, refreshTokenExpiry)
      checkForVersion()
      setFiltersCtx()
      // setInitialDateFilters();    This Method will be used to set initial date filter parameters once we move all date filters in hooks
      const accountData: any = await getRequest('/merchant-user/profile', apiURI.AUTH_ENGINE)
      const data = accountData?.data?.data
      const activeStore = getActiveStore(data?.organizations)
      setLoaderProperties({ isLoaderVisible: false })
      setIsLoading(false)
      const onboardingResponse: any = await getRequest(`/organization/${activeStore?.merchant_id}`, apiURI.AUTH_ENGINE)
      setOnboardingData(onboardingResponse?.data?.data)
      if (Boolean(activeStore?.in_onboarding)) {
        setActiveShop(activeStore?.merchant_id)
        router.push('/onboarding')
        return
      }
      router.replace('/')
    } catch (error: any) {
      setIsLoading(false)
      setLoaderProperties({ isLoaderVisible: false })
      if (showOTPPage) {
        setOtpError(true)
        setOtpErrorMessage(t('something_went_wrong'))
      }
    }
  }

  const handleResendOtp = async () => {
    setLoaderProperties({ isLoaderVisible: true, message: 'Resending OTP...' })
    setIsLoading(true)
    try {
      const response: any = await postRequest('/merchant-user/resend', apiURI.AUTH_ENGINE, {
        context_id: otpContext
      })
      if (!response?.data?.success) {
        setOtpErrorMessage(t('something_went_wrong'))
        setOtpError(true)
        setLoaderProperties({ isLoaderVisible: false })
        setIsLoading(false)
        return
      }
      setOtpCounter(30)
      setOtpError(false)
      setOtpErrorMessage('')
      successToast(t('otp_sent'))
    } catch (error) {
      console.error(error)
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
      setIsLoading(false)
    }
  }

  return (
    <>
      {showOTPPage ? (
        <div className='flex flex-col justify-between border w-[26.5rem] bg-[#FFFFFF] rounded-2xl px-4 gap-4 pt-12 pb-8 min-h-[17rem]'>
          <div className='flex flex-col space-y-6'>
            <p className='text-sm font-medium text-center text-coal-dark'>
              {t('enter_otp_sent_to')}&nbsp;
              <span className='font-semibold text-coal-dark'>{email}</span>
            </p>
            <div className='flex flex-col space-y-2'>
              <OTPInput
                OTPLength={6}
                value={otpValue}
                onChange={value => setOtpValue(value)}
                hasError={(otpError && !isLoading) || false}
                autoFocus={true}
                inputType={'tel'}
                disabled={false}
              />
              {otpError && <span className='text-sm text-center text-red-dark'>{otpErrorMessage}</span>}
            </div>
            {Boolean(otpCounter) ? (
              <span className='text-sm font-medium text-center text-coal-dark'>
                {t('resend_otp_timer')}&nbsp;{otpCounter}
              </span>
            ) : (
              <span
                onClick={handleResendOtp}
                className='text-sm font-semibold cursor-pointer text-center text-coal-dark underline underline-offset-4 decoration-gray-dark decoration-dashed'
              >
                {t('resend_otp_via_email')}
              </span>
            )}
          </div>
          <div className='text-sm text-center'>
            <p className='text-carbon inline-flex items-center justify-center text-center'>
              <ChevronLeft className='w-4 h-4' />
              &nbsp;
              <span onClick={() => setShowOTPPage(false)} className='font-semibold text-coal-dark cursor-pointer'>
                {t('go_back')}
              </span>
            </p>
          </div>
        </div>
      ) : (
        <form
          noValidate
          autoComplete='off'
          onSubmit={e => e.preventDefault()}
          className='flex flex-col border w-[26.5rem] bg-[#FFFFFF] rounded-2xl p-4 gap-4'
        >
          <div className=''>
            <InputTextField
              label={t('email')}
              inputProps={{
                style: {
                  marginLeft: -16,
                  marginRight: -16,
                  paddingLeft: 14,
                  paddingRight: 14,
                  width: '104%',
                  fontSize: 14
                }
              }}
              textFieldId='email'
              inputType='email'
              inputPlaceholder={t('email_placeholder')}
              isRequired={true}
              inputValue={email}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (emailError) {
                  setEmailError(false)
                }
                setEmail(event.target.value)
              }}
              errorStatus={emailError}
              errorMessage={t('valid_email')}
            />
          </div>
          <InputTextField
            label={t('enter_password')}
            inputPlaceholder={t('password_placeholder')}
            inputProps={{
              style: {
                marginLeft: -16,
                marginRight: -8,
                paddingLeft: 16,
                paddingRight: 14,
                width: '104%',
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                fontSize: 14
              }
            }}
            textFieldId='password'
            handleChange={handlePasswordChange('password')}
            inputValue={password}
            inputType={values.showPassword ? 'text' : 'password'}
            handleKeyDown={event => {
              if (event.key === 'Enter') {
                validateCredentials()
              }
            }}
            endIcon={
              <button
                className={`w-10 h-9 flex justify-center items-center rounded-br-lg rounded-tr-lg
        transition duration-300 ${values.showPassword ? 'bg-[#f0f0f0]' : 'bg-gray-lightest'} hover:bg-[#e0e0e0] -mr-2`}
                aria-label='toggle password visibility'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
              >
                {values.showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            }
            errorStatus={passwordError}
            errorMessage={t('valid_password')}
          />

          {/* {credentailsInvalidMessage && <p className='text-sm text-center text-[#f44336]'>{credentailsInvalidMessage}</p>} */}

          <div className=''>
            <button
              disabled={!Boolean(email.trim() && password.trim()) || isLoading || loaderConfig.isLoaderVisible}
              onClick={validateCredentials}
              type='submit'
              className='w-full flex justify-center items-center h-8 text-sm font-semibold rounded-lg disabled:bg-[#E0E0E0] bg-primary-dark disabled:cursor-not-allowed	disabled:text-[#949494] text-[#ffffff]'
            >
              {isLoading ? <Loader style={{ height: 16, width: 16 }} /> : t('sign_in')}
            </button>
          </div>
          {invoiceError && (
            <div className='text-sm text-center text-red-dark'>
              {invoiceError}
              <span onClick={callShowMerchantInvoiceUnpaid} className='cursor-pointer text-blue-500 '>
                {' '}
                {t('login.click_here')}
              </span>
            </div>
          )}
          <div className='text-sm font-semibold text-center text-coal-dark'>
            <Link href='/auth/reset-password'>{t('reset_password')}</Link>
          </div>
        </form>
      )}
      {/* {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null} */}
    </>
  )
}

export default Login
